export const schemaVersion = 1;

export function createDocument() {
  return {
    schemaVersion,
    properties: [],
    assets: [],
    assignments: [],
    allocations: [],
    policies: [],
    browserViews: []
  };
}

export function createProperty(document, draft, createId = defaultCreateId) {
  assertUniquePropertyName(document, draft.name);
  const property = propertyFromDraft(document, draft, createId);
  return {
    ...document,
    properties: [...document.properties, property]
  };
}

export function moveProperty(document, fromIndex, toIndex) {
  if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) return document;
  if (fromIndex < 0 || fromIndex >= document.properties.length || toIndex < 0 || toIndex >= document.properties.length || fromIndex === toIndex) return document;
  const properties = [...document.properties];
  const [property] = properties.splice(fromIndex, 1);
  properties.splice(toIndex, 0, property);
  return { ...document, properties };
}

export function createAsset(document, draft) {
  validateAssetOrThrow(document, draft);
  const id = draft.id.trim();
  return {
    ...document,
    assets: [...document.assets, { id, name: draft.name.trim() }],
    assignments: replaceAssetAssignments(document, id, draft.assignments)
  };
}

export function moveAsset(document, fromIndex, toIndex) {
  if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) return document;
  if (fromIndex < 0 || fromIndex >= document.assets.length || toIndex < 0 || toIndex >= document.assets.length || fromIndex === toIndex) return document;
  const assets = [...document.assets];
  const [asset] = assets.splice(fromIndex, 1);
  assets.splice(toIndex, 0, asset);
  return { ...document, assets };
}

export function updateAsset(document, assetId, draft) {
  findAsset(document, assetId);
  validateAssetOrThrow(document, draft, assetId);
  const id = draft.id.trim();
  return {
    ...document,
    assets: document.assets.map((asset) => (
      asset.id === assetId ? { id, name: draft.name.trim() } : asset
    )),
    assignments: replaceAssetAssignments(document, assetId, draft.assignments, id),
    allocations: (document.allocations ?? []).map((allocation) => (
      allocation.assetId === assetId ? { ...allocation, assetId: id } : allocation
    ))
  };
}

export function deleteAsset(document, assetId) {
  findAsset(document, assetId);
  return {
    ...document,
    assets: document.assets.filter((asset) => asset.id !== assetId),
    assignments: document.assignments.filter((assignment) => assignment.assetId !== assetId),
    allocations: (document.allocations ?? []).filter((allocation) => allocation.assetId !== assetId)
  };
}

export function updateAllocationSnapshot(document, allocations) {
  const seenAssetIds = new Set();
  for (const allocation of allocations) {
    if (!document.assets.some((asset) => asset.id === allocation.assetId)) {
      throw new Error(`Unknown asset: ${allocation.assetId}`);
    }
    if (seenAssetIds.has(allocation.assetId)) throw new Error("Each asset can have only one allocation.");
    if (!Number.isInteger(allocation.basisPoints) || allocation.basisPoints < 0 || allocation.basisPoints > 10000) {
      throw new Error("Allocations must be between 0% and 100% with no more than two decimal places.");
    }
    seenAssetIds.add(allocation.assetId);
  }
  return {
    ...document,
    allocations: allocations.filter((allocation) => allocation.basisPoints > 0)
  };
}

export function sortAssetsByAllocation(document) {
  const basisPointsByAssetId = new Map((document.allocations ?? []).map((allocation) => [allocation.assetId, allocation.basisPoints]));
  return {
    ...document,
    assets: document.assets.map((asset, index) => ({ asset, index }))
      .sort((left, right) => (basisPointsByAssetId.get(right.asset.id) ?? 0) - (basisPointsByAssetId.get(left.asset.id) ?? 0) || left.index - right.index)
      .map(({ asset }) => asset)
  };
}

export function createPolicy(document, draft, createId = defaultCreateId) {
  validatePolicyOrThrow(document, draft);
  return { ...document, policies: [...(document.policies ?? []), { ...structuredClone(draft), id: createId() }] };
}

export function updatePolicy(document, policyId, draft) {
  findPolicy(document, policyId);
  validatePolicyOrThrow(document, draft, policyId);
  return {
    ...document,
    policies: document.policies.map((policy) => policy.id === policyId ? { ...structuredClone(draft), id: policyId } : policy)
  };
}

export function deletePolicy(document, policyId) {
  findPolicy(document, policyId);
  return { ...document, policies: document.policies.filter((policy) => policy.id !== policyId) };
}

export function createBrowserView(document, draft, createId = defaultCreateId) {
  validateBrowserViewOrThrow(document, draft);
  assertUniqueBrowserViewName(document, draft.name);
  return { ...document, browserViews: [...(document.browserViews ?? []), browserViewFromDraft(draft, createId())] };
}

export function updateBrowserView(document, viewId, draft) {
  if (!(document.browserViews ?? []).some((view) => view.id === viewId)) throw new Error(`Unknown browser view: ${viewId}`);
  validateBrowserViewOrThrow(document, draft);
  assertUniqueBrowserViewName(document, draft.name, viewId);
  return {
    ...document,
    browserViews: document.browserViews.map((view) => view.id === viewId ? browserViewFromDraft(draft, viewId) : view)
  };
}

export function deleteBrowserView(document, viewId) {
  if (!(document.browserViews ?? []).some((view) => view.id === viewId)) throw new Error(`Unknown browser view: ${viewId}`);
  return { ...document, browserViews: document.browserViews.filter((view) => view.id !== viewId) };
}

export function validateBrowserViewDraft(document, draft) {
  if (!draft.name?.trim()) return "Enter a view name.";
  if (!Array.isArray(draft.groupBy)) return "Select valid grouping properties.";
  if (draft.groupBy.length > 3 || new Set(draft.groupBy).size !== draft.groupBy.length) return "Select up to three different grouping properties.";
  if (draft.groupBy.some((propertyId) => !document.properties.some((property) => property.id === propertyId))) return "Select valid grouping properties.";
  if (draft.displayProperties !== undefined) {
    if (!Array.isArray(draft.displayProperties)) return "Select valid decoration properties.";
    if (draft.displayProperties.length > 3 || new Set(draft.displayProperties).size !== draft.displayProperties.length) return "Select up to three different decoration properties.";
    if (draft.displayProperties.some((propertyId) => !document.properties.some((property) => property.id === propertyId))) return "Select valid decoration properties.";
  }
  if (!["allocation-desc", "allocation-asc", "configured", "decoration", "name"].includes(draft.sort)) return "Select a valid sort order.";
  if (draft.sort === "decoration" && !draft.displayProperties?.length) return "Select a decoration property to sort by its value.";
  if (draft.filter) {
    const property = document.properties.find((item) => item.id === draft.filter.propertyId);
    if (!property?.values.some((value) => value.id === draft.filter.valueId)) return "Select a valid filter value.";
  }
  return null;
}

export function validatePolicyDraft(document, draft, excludedPolicyId) {
  if (draft.enabled !== undefined && typeof draft.enabled !== "boolean") return "Choose whether the policy is enabled.";
  const scopeError = validatePolicyScope(document, draft.scope);
  if (scopeError) return scopeError;
  const duplicateError = duplicatePolicyScopeError(document, draft.scope, excludedPolicyId);
  if (duplicateError) return duplicateError;

  return validatePolicyRanges(draft);
}

function validatePolicyRanges(draft) {
  const mustError = validatePolicyBounds(draft.must, true);
  if (mustError) return mustError;
  const shouldError = validatePolicyBounds(draft.should, false);
  if (shouldError) return shouldError;
  if (draft.should?.minimum === undefined && draft.should?.maximum === undefined) return null;
  if (
    (draft.must.minimum !== undefined && draft.should.minimum !== undefined && draft.should.minimum < draft.must.minimum)
    || (draft.must.maximum !== undefined && draft.should.maximum !== undefined && draft.should.maximum > draft.must.maximum)
    || (draft.should.minimum !== undefined && draft.must.maximum !== undefined && draft.should.minimum > draft.must.maximum)
    || (draft.should.maximum !== undefined && draft.must.minimum !== undefined && draft.should.maximum < draft.must.minimum)
  ) {
    return "The Should condition must be inside the Must condition.";
  }
  return null;
}

function duplicatePolicyScopeError(document, scope, excludedPolicyId) {
  const duplicate = (document.policies ?? []).find((policy) => (
    policy.id !== excludedPolicyId && samePolicyScope(policy.scope, scope)
  ));
  return duplicate ? `A policy already exists for ${policyScopeLabel(document, scope)}.` : null;
}

export function samePolicyScope(left, right) {
  if (!left || !right) return false;
  if (left.type !== right.type) return false;
  if (left.type === "specific") return left.propertyId === right.propertyId && left.valueId === right.valueId;
  if (left.type !== "concentration" || !left.dimension || !right.dimension) return false;
  if (left.dimension.type !== right.dimension.type) return false;
  return left.dimension.type === "asset" || left.dimension.propertyId === right.dimension.propertyId;
}

function policyScopeLabel(document, scope) {
  if (scope.type === "specific") {
    const property = document.properties.find((item) => item.id === scope.propertyId);
    const value = property.values.find((item) => item.id === scope.valueId);
    return `${value.name} ${property.name}`;
  }
  if (scope.dimension.type === "asset") return "Any Asset";
  const property = document.properties.find((item) => item.id === scope.dimension.propertyId);
  return `Any ${property.name}`;
}

function validatePolicyScope(document, scope) {
  if (scope?.type === "specific") {
    const property = document.properties.find((item) => item.id === scope.propertyId);
    if (!property) return "Select a property.";
    if (!property.values.some((value) => value.id === scope.valueId)) return "Select a property value.";
  } else if (scope?.type === "concentration") {
    const dimension = scope.dimension;
    if (dimension?.type !== "asset" && dimension?.type !== "property") return "Select a policy dimension.";
    if (dimension.type === "property" && !document.properties.some((property) => property.id === dimension.propertyId)) {
      return "Select a property.";
    }
  } else {
    return "Select a policy scope.";
  }
  return null;
}

function validatePolicyBounds(bounds, required) {
  const values = [bounds?.minimum, bounds?.maximum].filter((value) => value !== undefined);
  if (required && bounds?.maximum === undefined) return "MUST not exceed is required.";
  if (!values.every(validBasisPoints)) {
    return "Enter percentages from 0 to 100 with up to two decimal places.";
  }
  if (bounds?.minimum !== undefined && bounds?.maximum !== undefined && bounds.minimum > bounds.maximum) return "Minimums cannot exceed maximums.";
  return null;
}

export function evaluatePolicy(document, policy) {
  validatePolicyOrThrow(document, policy, policy.id);
  if (policy.scope.type === "specific") {
    const property = findProperty(document, policy.scope.propertyId);
    const value = property.values.find((item) => item.id === policy.scope.valueId);
    const basisPoints = document.assets.reduce((total, asset) => (
      effectiveValue(document, asset.id, property.id).id === value.id
        ? total + assetAllocation(document, asset.id)
        : total
    ), 0);
    return [{ key: value.id, label: `${property.name}: ${value.name}`, basisPoints, ...evaluateRanges(policy, basisPoints) }];
  }

  if (policy.scope.dimension.type === "asset") {
    return document.assets.map((asset) => ({
      key: asset.id,
      label: asset.id,
      basisPoints: assetAllocation(document, asset.id),
      ...evaluateRanges(policy, assetAllocation(document, asset.id))
    }));
  }

  const property = findProperty(document, policy.scope.dimension.propertyId);
  const representedValueIds = new Set(document.assets.map((asset) => effectiveValue(document, asset.id, property.id).id));
  return property.values.filter((value) => representedValueIds.has(value.id)).map((value) => {
    const basisPoints = document.assets.reduce((total, asset) => (
      effectiveValue(document, asset.id, property.id).id === value.id
        ? total + assetAllocation(document, asset.id)
        : total
    ), 0);
    return { key: value.id, label: value.name, basisPoints, ...evaluateRanges(policy, basisPoints) };
  });
}

export function validateAssetDraft(document, draft, excludedAssetId) {
  if (!draft.id?.trim()) return "Enter an asset ID or ticker.";
  if (!draft.name?.trim()) return "Enter an asset name.";
  const normalizedId = draft.id.trim().toLocaleLowerCase();
  if (document.assets.some((asset) => (
    asset.id !== excludedAssetId && asset.id.trim().toLocaleLowerCase() === normalizedId
  ))) return "Asset IDs must be unique.";
  for (const assignment of draft.assignments ?? []) {
    const property = document.properties.find((item) => item.id === assignment.propertyId);
    if (!property?.values.some((value) => value.id === assignment.valueId)) {
      return "Select a valid value for every property.";
    }
  }
  return null;
}

function validateAssetOrThrow(document, draft, excludedAssetId) {
  const error = validateAssetDraft(document, draft, excludedAssetId);
  if (error) throw new Error(error);
}

export function updateProperty(document, propertyId, draft) {
  findProperty(document, propertyId);
  assertUniquePropertyName(document, draft.name, propertyId);
  validateOrThrow(document, draft, propertyId);
  const remainingValueIds = new Set(draft.values.map((value) => value.id));
  return {
    ...document,
    properties: document.properties.map((property) => (
      property.id === propertyId
        ? {
            ...property,
            name: draft.name.trim(),
            values: draft.values.map(valueFromDraft)
          }
        : {
            ...property,
            values: property.values.map((value) => (
              value.visibility?.propertyId === propertyId && !remainingValueIds.has(value.visibility.valueId)
                ? withoutVisibility(value)
                : value
            ))
          }
    )),
    assignments: document.assignments.filter((assignment) => (
      assignment.propertyId !== propertyId
      || draft.values.some((value) => value.id === assignment.valueId)
    )),
    policies: (document.policies ?? []).filter((policy) => (
      policy.scope.type !== "specific"
      || policy.scope.propertyId !== propertyId
      || draft.values.some((value) => value.id === policy.scope.valueId)
    )),
    browserViews: (document.browserViews ?? []).map((view) => (
      view.filter?.propertyId === propertyId && !draft.values.some((value) => value.id === view.filter.valueId)
        ? { ...view, filter: null }
        : view
    ))
  };
}

export function deleteProperty(document, propertyId) {
  findProperty(document, propertyId);
  return {
    ...document,
    properties: document.properties.filter((property) => property.id !== propertyId).map((property) => ({
      ...property,
      values: property.values.map((value) => value.visibility?.propertyId === propertyId ? withoutVisibility(value) : value)
    })),
    assignments: document.assignments.filter((assignment) => assignment.propertyId !== propertyId),
    policies: (document.policies ?? []).filter((policy) => !policyReferencesProperty(policy, propertyId)),
    browserViews: (document.browserViews ?? []).map((view) => {
      const displayProperties = (view.displayProperties ?? []).filter((displayPropertyId) => displayPropertyId !== propertyId);
      return {
        ...view,
        groupBy: view.groupBy.filter((groupPropertyId) => groupPropertyId !== propertyId),
        displayProperties,
        sort: view.sort === "decoration" && displayProperties.length === 0 ? "name" : view.sort,
        filter: view.filter?.propertyId === propertyId ? null : view.filter
      };
    })
  };
}

export function countPropertyAssignments(document, propertyId) {
  return document.assignments.filter((assignment) => assignment.propertyId === propertyId).length;
}

export function countValueAssignments(document, propertyId, valueId) {
  return document.assignments.filter((assignment) => (
    assignment.propertyId === propertyId && assignment.valueId === valueId
  )).length;
}

export function countPropertyPolicyReferences(document, propertyId) {
  return (document.policies ?? []).filter((policy) => policyReferencesProperty(policy, propertyId)).length;
}

export function countValuePolicyReferences(document, propertyId, valueId) {
  return (document.policies ?? []).filter((policy) => (
    policy.scope.type === "specific"
    && policy.scope.propertyId === propertyId
    && policy.scope.valueId === valueId
  )).length;
}

export function countPropertyBrowserViewReferences(document, propertyId) {
  return (document.browserViews ?? []).filter((view) => view.groupBy.includes(propertyId) || view.displayProperties?.includes(propertyId) || view.filter?.propertyId === propertyId).length;
}

export function countPropertyVisibilityCriteria(document, propertyId) {
  return document.properties.reduce((count, property) => (
    count + property.values.filter((value) => value.visibility?.propertyId === propertyId).length
  ), 0);
}

export function countValueVisibilityCriteria(document, propertyId, valueId) {
  return document.properties.reduce((count, property) => (
    count + property.values.filter((value) => (
      value.visibility?.propertyId === propertyId && value.visibility.valueId === valueId
    )).length
  ), 0);
}

export function effectiveValue(document, assetId, propertyId) {
  const property = findProperty(document, propertyId);
  const assignment = document.assignments.find((item) => (
    item.assetId === assetId && item.propertyId === propertyId
  ));
  return property.values.find((value) => value.id === assignment?.valueId) ?? property.values[0];
}

export function visiblePropertyValues(document, assignments, propertyId) {
  const property = findProperty(document, propertyId);
  return property.values.filter((value) => {
    if (!value.visibility) return true;
    const visibilityProperty = findProperty(document, value.visibility.propertyId);
    const assignment = assignments.find((item) => item.propertyId === visibilityProperty.id);
    const effectiveVisibilityValue = visibilityProperty.values.find((item) => item.id === assignment?.valueId) ?? visibilityProperty.values[0];
    return effectiveVisibilityValue.id === value.visibility.valueId;
  });
}

export function validatePropertyDraft(draft, document, propertyId = draft.id) {
  if (!draft.name?.trim()) return "Enter a property name.";
  if (!draft.values?.length) return "Add at least one possible value.";
  if (draft.values.some((value) => !value.name?.trim())) return "Possible values cannot be empty.";

  const normalizedValues = draft.values.map((value) => value.name.trim().toLocaleLowerCase());
  if (new Set(normalizedValues).size !== normalizedValues.length) {
    return "Possible values must be unique.";
  }
  if (draft.values[0].visibility) return "The default value cannot have visibility criteria.";
  if (document && draft.values.some((value) => value.visibility)) {
    const controlsVisibility = propertyId && document.properties.some((property) => (
      property.id !== propertyId
      && property.values.some((value) => value.visibility?.propertyId === propertyId)
    ));
    if (controlsVisibility) return "A property used by visibility criteria cannot have criteria of its own.";
    for (const value of draft.values) {
      if (!value.visibility) continue;
      const visibilityProperty = document.properties.find((property) => property.id === value.visibility.propertyId);
      if (!visibilityProperty || visibilityProperty.id === propertyId) return "Choose a valid visibility property.";
      if (visibilityProperty.values.some((item) => item.visibility)) return "Visibility criteria cannot be nested.";
      if (!visibilityProperty.values.some((item) => item.id === value.visibility.valueId)) return "Choose a valid visibility value.";
    }
  }
  return null;
}

function propertyFromDraft(document, draft, createId) {
  validateOrThrow(document, draft);
  return {
    id: createId(),
    name: draft.name.trim(),
    values: draft.values.map((value) => valueFromDraft({ ...value, id: value.id ?? createId() }))
  };
}

function validateOrThrow(document, draft, propertyId) {
  const error = validatePropertyDraft(draft, document, propertyId);
  if (error) throw new Error(error);
}

function valueFromDraft(value) {
  return {
    id: value.id,
    name: value.name.trim(),
    ...(value.visibility ? { visibility: { ...value.visibility } } : {})
  };
}

function withoutVisibility({ visibility: _visibility, ...value }) {
  return value;
}

function findProperty(document, propertyId) {
  const property = document.properties.find((item) => item.id === propertyId);
  if (!property) throw new Error(`Unknown property: ${propertyId}`);
  return property;
}

function findAsset(document, assetId) {
  const asset = document.assets.find((item) => item.id === assetId);
  if (!asset) throw new Error(`Unknown asset: ${assetId}`);
  return asset;
}

function findPolicy(document, policyId) {
  const policy = (document.policies ?? []).find((item) => item.id === policyId);
  if (!policy) throw new Error(`Unknown policy: ${policyId}`);
  return policy;
}

function validatePolicyOrThrow(document, draft, excludedPolicyId) {
  const error = validatePolicyDraft(document, draft, excludedPolicyId);
  if (error) throw new Error(error);
}

function validateBrowserViewOrThrow(document, draft) {
  const error = validateBrowserViewDraft(document, draft);
  if (error) throw new Error(error);
}

function browserViewFromDraft(draft, id) {
  return {
    id,
    name: draft.name.trim(),
    groupBy: [...draft.groupBy],
    displayProperties: [...(draft.displayProperties ?? [])],
    sort: draft.sort,
    filter: draft.filter ? { ...draft.filter } : null
  };
}

function validBasisPoints(value) {
  return Number.isInteger(value) && value >= 0 && value <= 10000;
}

function assetAllocation(document, assetId) {
  return (document.allocations ?? []).find((allocation) => allocation.assetId === assetId)?.basisPoints ?? 0;
}

function evaluateRanges(policy, basisPoints) {
  const hasShould = policy.should?.minimum !== undefined || policy.should?.maximum !== undefined;
  const compliant = boundsMatch(policy.must, basisPoints);
  const onTarget = compliant && (!hasShould || boundsMatch(policy.should, basisPoints));
  return { compliant, onTarget, status: !compliant ? "non-compliant" : hasShould && !onTarget ? "warning" : hasShould ? "on-target" : "compliant" };
}

function boundsRange(bounds) {
  return { minimum: bounds?.minimum ?? 0, maximum: bounds?.maximum ?? 10000 };
}

function boundsMatch(bounds, basisPoints) {
  const range = boundsRange(bounds);
  return basisPoints >= range.minimum && basisPoints <= range.maximum;
}

function policyReferencesProperty(policy, propertyId) {
  return (policy.scope.type === "specific" && policy.scope.propertyId === propertyId)
    || (policy.scope.type === "concentration" && policy.scope.dimension.type === "property" && policy.scope.dimension.propertyId === propertyId);
}

function replaceAssetAssignments(document, assetId, assignments = [], nextAssetId = assetId) {
  return [
    ...document.assignments.filter((assignment) => assignment.assetId !== assetId),
    ...assignments.map((assignment) => ({ assetId: nextAssetId, ...assignment }))
  ];
}

function assertUniquePropertyName(document, name, excludedPropertyId) {
  const normalizedName = name?.trim().toLocaleLowerCase();
  const duplicate = document.properties.some((property) => (
    property.id !== excludedPropertyId
    && property.name.trim().toLocaleLowerCase() === normalizedName
  ));
  if (duplicate) throw new Error("Property names must be unique.");
}

function assertUniqueBrowserViewName(document, name, excludedViewId) {
  const normalizedName = name?.trim().toLocaleLowerCase();
  if ((document.browserViews ?? []).some((view) => view.id !== excludedViewId && view.name.trim().toLocaleLowerCase() === normalizedName)) {
    throw new Error("View names must be unique.");
  }
}

function defaultCreateId() {
  return crypto.randomUUID();
}
